/** How long one company-list refresh may take before it is aborted and the queue moves on. */
export const REFRESH_MS = 10_000

interface RefreshQueueOptions<R, A> {
  /** One request. `null` is the failure result; the signal aborts it at the deadline. */
  request: (signal: AbortSignal) => Promise<R | null>
  /** Applies a request's answer and says what its callers receive. Runs only for a current answer. */
  apply: (result: R) => A | null
  deadlineMs: number
}

/**
 * Company-list refreshes, one at a time (GRO-82 design, D5).
 *
 * A creation, a reconciliation and the accounts panel's connect can all refresh the list,
 * and an older answer landing after a newer one would drop a company that was just
 * created. So one request is open at a time. A call made while one is in flight waits,
 * then shares **one** follow-up with every other call that arrived meanwhile; the
 * follow-up starts after the in-flight request settled, so it postdates every mutation
 * that prompted it. A request that passes its deadline is aborted, resolves its callers
 * with `null` and releases the queue, and an answer that still arrives for it is dropped.
 */
export function createRefreshQueue<R, A>({ request, apply, deadlineMs }: RefreshQueueOptions<R, A>): () => Promise<A | null> {
  let busy = false
  let waiting: Array<(value: A | null) => void> = []

  function send(callers: Array<(value: A | null) => void>): Promise<void> {
    const controller = new AbortController()
    return new Promise((release) => {
      let settled = false
      const finish = (value: A | null) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        for (const caller of callers) caller(value)
        release()
      }
      const timer = setTimeout(() => {
        controller.abort()
        finish(null)
      }, deadlineMs)
      request(controller.signal).then(
        (result) => {
          if (settled || result === null) finish(null)
          else finish(apply(result))
        },
        () => finish(null),
      )
    })
  }

  function pump() {
    if (busy || waiting.length === 0) return
    const callers = waiting
    waiting = []
    busy = true
    void send(callers).then(() => {
      busy = false
      pump()
    })
  }

  return () => new Promise<A | null>((resolve) => {
    waiting.push(resolve)
    pump()
  })
}
